import { Trans } from '@lingui/macro';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Box, Button, Flex, Text } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { AvatarType, Size } from '../../Avatar/Avatar';
import StackedAvatar from '../../StackedAvatar/StackedAvatar';
import { ShortlistContactButtonClickEvent, ShortlistOpenEvent } from '../analyticsEvents';
import { MAX_PREVIEW_AVATARS, MIN_PREVIEW_AVATARS, MULTI_CONTACT_ROUTE } from '../constants';
import useShortlist from '../hooks/useShortlist';

import NewBadge from './NewBadge';

export const buildPreviewImageUrls = (count: number, loadedImageUrls: string[]): string[] => {
  const slots = Math.max(MIN_PREVIEW_AVATARS, Math.min(count, MAX_PREVIEW_AVATARS));
  const loaded = loadedImageUrls.slice(0, slots);
  return [...loaded, ...Array.from({ length: slots - loaded.length }, () => '')];
};

const ShortlistPreview = (): JSX.Element | null => {
  const isMobile = useMatchMedia(MQ.MOBILE.getBreakpointString());
  const { isVisible, count, getSitters, setIsModalVisible } = useShortlist();
  const theme = useTheme();

  if (!isVisible) {
    return null;
  }

  const isEmpty = count === 0;

  const loadedImageUrls = getSitters()
    .map((sitter) => sitter.defaultImage.medium || sitter.defaultImage.small)
    .filter(Boolean);
  const imageUrls = buildPreviewImageUrls(count, loadedImageUrls);

  const handleLinkPress = (): void => {
    emitAnalyticsEvent(new ShortlistOpenEvent({ source: 'preview', count }));
    setIsModalVisible(true);
  };

  const handleButtonPress = (): void => {
    if (isEmpty) {
      emitAnalyticsEvent(new ShortlistOpenEvent({ source: 'preview', count }));
      setIsModalVisible(true);
    } else {
      new ShortlistContactButtonClickEvent({ source: 'preview', count }).fireCrossPageEvent();
    }
  };

  return (
    <Box
      position="fixed"
      bottom="0"
      left="0"
      right="0"
      zIndex={String(ZIndex.NAVBAR_FIXED.toNumber())}
      display="flex"
      justifyContent="center"
      px={['0x', '4x']}
      pb={['0x', '4x']}
      sx={{ pointerEvents: 'none', borderRadius: '10x' }}
    >
      <Flex
        width="100%"
        maxWidth={['100%', '600px']}
        flexDirection={['column', 'row']}
        alignItems={['stretch', 'center']}
        justifyContent="space-between"
        gap="4x"
        background="primary"
        borderRadius={['10x', 'round']}
        variant="elevation_mid"
        px="4x"
        py="4x"
        sx={{
          pointerEvents: 'auto',
          borderTopLeftRadius: ['16px', undefined],
          borderTopRightRadius: ['16px', undefined],
        }}
      >
        <Flex flexDirection="row" alignItems="center" gap="3x" minWidth="0">
          <StackedAvatar
            imageUrls={imageUrls}
            avatarType={AvatarType.Person}
            size={isMobile ? Size.ExtraSmall : Size.Small}
            avatarPropsOverrideCallback={(imageUrl) =>
              imageUrl === ''
                ? {
                    borderColor: theme.colors.border.primary,
                    borderStyle: 'dashed',
                  }
                : {
                    borderColor: theme.colors.neutral.white,
                    borderStyle: 'solid',
                  }
            }
          />
          {isEmpty ? (
            <Flex flexDirection="column">
              <Flex flexDirection="row" gap="2x">
                <Text size="200" fontWeight="semibold">
                  <Trans>Your contact list</Trans>
                </Text>
                <NewBadge />
              </Flex>
              <Text>
                <Trans>Add sitters and message them all at once</Trans>
              </Text>
            </Flex>
          ) : (
            <Button variant="link" onPress={handleLinkPress} sx={{ color: 'text.primary' }}>
              <Trans>View contact list ({count})</Trans>
            </Button>
          )}
        </Flex>
        <Box width={['100%', 'auto']} flexShrink="0">
          <Button
            variant="primary"
            fullWidth
            href={!isEmpty ? MULTI_CONTACT_ROUTE : undefined}
            onPress={handleButtonPress}
          >
            {isEmpty ? <Trans>View</Trans> : <Trans>Contact all</Trans>}
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};

export default ShortlistPreview;
